/**
 * Pure geometry helpers for the inactive-tab background pills rendered by
 * `ContentUnionShape`. Extracted from the component so the layout maths can
 * be unit-tested without spinning up the whole bump pipeline.
 */

export interface PillRect {
  /** X position relative to the window frame. */
  x: number
  /** Y position relative to the window frame. */
  y: number
  /** Pill width in pixels. */
  w: number
  /** Pill height in pixels. */
  h: number
}

/** Inactive-tab pill height — matches `.tab-bar__tab` height for non-active tabs. */
export const INACTIVE_PILL_HEIGHT = 28
/** Inactive-tab pill bottom margin — matches `.tab-bar__tab` margin-bottom. */
export const INACTIVE_PILL_MARGIN_BOTTOM = 6

/**
 * Compute the rect for an inactive tab's background pill.
 *
 * X position and width come from `tab.getBoundingClientRect()` so the pill
 * follows DnD-Kit's transforms during drag/sort — `offsetLeft`/`offsetWidth`
 * track layout (which doesn't reorder until drop) and would leave the pill
 * stranded at the tab's pre-drag position. Y position is anchored to the tab
 * container's bottom edge (not the tab's own rect) so the active-tab bump
 * animation cannot bleed vertical wobble into the inactive pills.
 *
 * Returns `null` when the tab has zero width (not laid out yet).
 */
export function computeInactivePillRect(
  tab: HTMLElement,
  frameRect: { left: number; top: number },
  tabsContainerRect: { bottom: number } | null,
): PillRect | null {
  const tabRect = tab.getBoundingClientRect()
  if (tabRect.width === 0) return null

  const x = tabRect.left - frameRect.left
  const w = tabRect.width

  let y: number
  if (tabsContainerRect) {
    const containerBottom = tabsContainerRect.bottom - frameRect.top
    y = containerBottom - INACTIVE_PILL_MARGIN_BOTTOM - INACTIVE_PILL_HEIGHT
  } else {
    y = tabRect.top - frameRect.top
  }

  return { x, y, w, h: INACTIVE_PILL_HEIGHT }
}
